// src/components/ui/TextInput/TextInput.tsx
import React, {useState, useRef, useCallback, useEffect} from 'react';
import {IonIcon} from '@ionic/react';
import {closeCircleOutline} from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Input visual variants */
export type TextInputVariant =
    | 'default'      // Standard input with border
    | 'ghost'        // Borderless, shows border on hover/focus
    | 'filled'       // Filled background
    | 'underline';   // Only bottom border

/** Input sizes */
export type TextInputSize = 'sm' | 'md' | 'lg';

/** Addon slot content */
export interface TextInputAddon {
    /** Icon (ionicons string) - default for both platforms */
    icon?: string;
    /** iOS-specific icon (overrides icon on iOS) */
    ios?: string;
    /** Material Design icon (overrides icon on Android/web) */
    md?: string;
    /** Google Material Symbols icon name (e.g., "pin", "dialpad") */
    materialIcon?: string;
    /** Custom React node (overrides all icon props) */
    content?: React.ReactNode;
    /** Click handler */
    onClick?: () => void;
    /** Tooltip text */
    tooltip?: string;
    /** Visually hidden but accessible */
    ariaLabel?: string;
}

export interface TextInputProps {
    /** Current value */
    value: string;
    /** Change handler */
    onChange: (value: string) => void;
    /** Placeholder text */
    placeholder?: string;
    /** Visual variant */
    variant?: TextInputVariant;
    /** Size preset */
    size?: TextInputSize;
    /** Leading addon (before input) */
    leadingAddon?: TextInputAddon;
    /** Trailing addon (after input, before clear) */
    trailingAddon?: TextInputAddon;
    /** Show clear button when value exists */
    clearable?: boolean;
    /** Callback when cleared */
    onClear?: () => void;
    /** Disabled state */
    disabled?: boolean;
    /** Read-only state */
    readOnly?: boolean;
    /** Error state */
    error?: boolean;
    /** Error message */
    errorMessage?: string;
    /** Label above input */
    label?: string;
    /** Helper text below input */
    helperText?: string;
    /** Input type (for single-line) */
    type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'number';
    /** Auto-focus on mount */
    autoFocus?: boolean;
    /** Input name */
    name?: string;
    /** Input id */
    id?: string;
    /** Aria label */
    'aria-label'?: string;
    /** Additional className */
    className?: string;
    /** Inline styles */
    style?: React.CSSProperties;

    // ─── Multiline Features ───────────────────────────────────────────

    /** Enable multiline (textarea) */
    multiline?: boolean;
    /** Number of visible rows (for multiline) */
    rows?: number;
    /** Minimum rows (for auto-resize) */
    minRows?: number;
    /** Maximum rows (for auto-resize) */
    maxRows?: number;
    /** Auto-resize height based on content */
    autoResize?: boolean;
    /** Max character count (shows counter) */
    maxLength?: number;
    /** Show character counter */
    showCharacterCount?: boolean;

    // ─── Event Handlers ───────────────────────────────────────────────

    onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

// ═══════════════════════════════════════════════════════════════════
// SIZE PRESETS
// ═══════════════════════════════════════════════════════════════════

interface SizePreset {
    height: number;
    fontSize: number;
    paddingX: number;
    paddingY: number;
    iconSize: number;
    gap: number;
    labelSize: number;
    borderRadius: number;
    lineHeight: number;
}

const sizePresets: Record<TextInputSize, SizePreset> = {
    sm: {
        height: 36,
        fontSize: 14,
        paddingX: 10,
        paddingY: 8,
        iconSize: 16,
        gap: 8,
        labelSize: 12,
        borderRadius: 8,
        lineHeight: 1.5,
    },
    md: {
        height: 44,
        fontSize: 15,
        paddingX: 12,
        paddingY: 10,
        iconSize: 18,
        gap: 10,
        labelSize: 13,
        borderRadius: 10,
        lineHeight: 1.5,
    },
    lg: {
        height: 52,
        fontSize: 17,
        paddingX: 14,
        paddingY: 12,
        iconSize: 20,
        gap: 12,
        labelSize: 14,
        borderRadius: 12,
        lineHeight: 1.5,
    },
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const TextInput: React.FC<TextInputProps> = ({
    value,
    onChange,
    placeholder = '',
    variant = 'ghost',
    size = 'md',
    leadingAddon,
    trailingAddon,
    clearable = true,
    onClear,
    disabled = false,
    readOnly = false,
    error = false,
    errorMessage,
    label,
    helperText,
    type = 'text',
    autoFocus = false,
    name,
    id,
    'aria-label': ariaLabel,
    className,
    style,
    // Multiline
    multiline = false,
    rows = 3,
    minRows = 2,
    maxRows = 10,
    autoResize = true,
    maxLength,
    showCharacterCount = false,
    // Event handlers
    onFocus,
    onBlur,
    onKeyDown,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const sizes = sizePresets[size];
    const hasValue = value.length > 0;
    const showClearButton = clearable && hasValue && !disabled && !readOnly;
    const characterCount = value.length;
    const showCounter = showCharacterCount || (maxLength !== undefined);

    // Auto-resize textarea
    useEffect(() => {
        if (multiline && autoResize && textareaRef.current) {
            const textarea = textareaRef.current;
            textarea.style.height = 'auto';

            const lineHeightPx = sizes.fontSize * sizes.lineHeight;
            const minHeight = minRows * lineHeightPx + sizes.paddingY * 2;
            const maxHeight = maxRows * lineHeightPx + sizes.paddingY * 2;

            const scrollHeight = textarea.scrollHeight;
            const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

            textarea.style.height = `${newHeight}px`;
        }
    }, [value, multiline, autoResize, minRows, maxRows, sizes]);

    // Handle focus
    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(true);
        onFocus?.(e);
    }, [onFocus]);

    // Handle blur
    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(false);
        onBlur?.(e);
    }, [onBlur]);

    // Handle input change
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let newValue = e.target.value;
        if (maxLength !== undefined && newValue.length > maxLength) {
            newValue = newValue.slice(0, maxLength);
        }
        onChange(newValue);
    }, [onChange, maxLength]);

    // Handle clear
    const handleClear = useCallback(() => {
        onChange('');
        onClear?.();
        if (multiline) {
            textareaRef.current?.focus();
        } else {
            inputRef.current?.focus();
        }
    }, [onChange, onClear, multiline]);

    // ─── Variant Styles ───────────────────────────────────────────────

    const getContainerStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            display: 'flex',
            alignItems: multiline ? 'flex-start' : 'center',
            gap: sizes.gap,
            minHeight: multiline ? undefined : sizes.height,
            padding: multiline
                ? `${sizes.paddingY}px ${sizes.paddingX}px`
                : `0 ${sizes.paddingX}px`,
            borderRadius: sizes.borderRadius,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.5 : 1,
            position: 'relative',
        };

        const borderColor = error
            ? 'var(--ion-color-danger)'
            : isFocused
                ? 'var(--kff-forge-gold)'
                : isHovered
                    ? 'var(--ion-color-step-400)'
                    : 'var(--ion-color-step-200)';

        switch (variant) {
            case 'default':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    border: `1.5px solid ${borderColor}`,
                    boxShadow: isFocused ? '0 0 0 3px rgba(203, 160, 82, 0.15)' : undefined,
                };
            case 'ghost':
                return {
                    ...base,
                    background: 'transparent',
                    borderBottom: `1.5px solid ${isFocused || isHovered ? borderColor : 'transparent'}`,
                    borderRadius: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                };
            case 'filled':
                return {
                    ...base,
                    background: isFocused
                        ? 'var(--ion-color-step-50)'
                        : 'var(--ion-color-step-100)',
                    border: `1.5px solid ${isFocused ? borderColor : 'transparent'}`,
                    boxShadow: isFocused ? '0 0 0 3px rgba(203, 160, 82, 0.1)' : undefined,
                };
            case 'underline':
                return {
                    ...base,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `2px solid ${borderColor}`,
                    borderRadius: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                };
            default:
                return base;
        }
    };

    const inputStyles: React.CSSProperties = {
        flex: 1,
        width: '100%',
        minWidth: 0,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        fontFamily: 'var(--kff-data-font)',
        fontSize: sizes.fontSize,
        fontWeight: 500,
        color: 'var(--ion-text-color)',
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'text',
        resize: 'none',
        lineHeight: sizes.lineHeight,
    };

    // ─── Render ───────────────────────────────────────────────────────

    return (
        <div className={className} style={{...style, position: 'relative'}}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={id}
                    style={{
                        display: 'block',
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizes.labelSize,
                        fontWeight: 500,
                        color: error ? 'var(--ion-color-danger)' : 'var(--ion-color-medium)',
                        marginBottom: 6,
                    }}
                >
                    {label}
                </label>
            )}

            {/* Input Container */}
            <div
                style={getContainerStyles()}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={() => {
                    if (!disabled) {
                        if (multiline) {
                            textareaRef.current?.focus();
                        } else {
                            inputRef.current?.focus();
                        }
                    }
                }}
            >
                {/* Leading Addon */}
                {leadingAddon && (
                    <div
                        onClick={e => {
                            if (leadingAddon.onClick) {
                                e.stopPropagation();
                                leadingAddon.onClick();
                            }
                        }}
                        title={leadingAddon.tooltip}
                        aria-label={leadingAddon.ariaLabel}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: 'var(--ion-color-medium)',
                            cursor: leadingAddon.onClick ? 'pointer' : 'inherit',
                            transition: 'color 0.15s',
                            marginLeft: 4,
                            marginTop: multiline ? 2 : 0,
                        }}
                    >
                        {leadingAddon.content ? (
                            leadingAddon.content
                        ) : leadingAddon.materialIcon ? (
                            <span
                                className="material-symbols-outlined"
                                style={{
                                    fontSize: sizes.iconSize,
                                    width: sizes.iconSize,
                                    height: sizes.iconSize,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    lineHeight: 1,
                                }}
                            >
                                {leadingAddon.materialIcon}
                            </span>
                        ) : (leadingAddon.icon || leadingAddon.ios || leadingAddon.md) ? (
                            <IonIcon
                                icon={leadingAddon.icon}
                                ios={leadingAddon.ios}
                                md={leadingAddon.md}
                                style={{
                                    fontSize: sizes.iconSize,
                                    width: sizes.iconSize,
                                    height: sizes.iconSize,
                                }}
                            />
                        ) : null}
                    </div>
                )}

                {/* Input or Textarea */}
                <div style={{flex: 1, display: 'flex', alignItems: 'center', minWidth: 0}}>
                    {multiline ? (
                        <textarea
                            ref={textareaRef}
                            value={value}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            disabled={disabled}
                            readOnly={readOnly}
                            autoFocus={autoFocus}
                            name={name}
                            id={id}
                            aria-label={ariaLabel || label}
                            aria-invalid={error}
                            aria-describedby={errorMessage ? `${id}-error` : undefined}
                            rows={autoResize ? minRows : rows}
                            maxLength={maxLength}
                            style={inputStyles}
                        />
                    ) : (
                        <input
                            ref={inputRef}
                            type={type}
                            value={value}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            disabled={disabled}
                            readOnly={readOnly}
                            autoFocus={autoFocus}
                            name={name}
                            id={id}
                            aria-label={ariaLabel || label}
                            aria-invalid={error}
                            aria-describedby={errorMessage ? `${id}-error` : undefined}
                            maxLength={maxLength}
                            style={inputStyles}
                        />
                    )}
                </div>

                {/* Trailing Addon */}
                {trailingAddon && (
                    <div
                        onClick={e => {
                            if (trailingAddon.onClick) {
                                e.stopPropagation();
                                trailingAddon.onClick();
                            }
                        }}
                        title={trailingAddon.tooltip}
                        aria-label={trailingAddon.ariaLabel}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: 'var(--ion-color-medium)',
                            cursor: trailingAddon.onClick ? 'pointer' : 'inherit',
                            transition: 'color 0.15s',
                            marginTop: multiline ? 2 : 0,
                        }}
                    >
                        {trailingAddon.content ? (
                            trailingAddon.content
                        ) : trailingAddon.materialIcon ? (
                            <span
                                className="material-symbols-outlined"
                                style={{
                                    fontSize: sizes.iconSize,
                                    width: sizes.iconSize,
                                    height: sizes.iconSize,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    lineHeight: 1,
                                }}
                            >
                                {trailingAddon.materialIcon}
                            </span>
                        ) : (trailingAddon.icon || trailingAddon.ios || trailingAddon.md) ? (
                            <IonIcon
                                icon={trailingAddon.icon}
                                ios={trailingAddon.ios}
                                md={trailingAddon.md}
                                style={{
                                    fontSize: sizes.iconSize,
                                    width: sizes.iconSize,
                                    height: sizes.iconSize,
                                }}
                            />
                        ) : null}
                    </div>
                )}

                {/* Clear Button */}
                {showClearButton && (
                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            handleClear();
                        }}
                        aria-label="Clear input"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: 0,
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--ion-color-medium)',
                            cursor: 'pointer',
                            opacity: isHovered || isFocused ? 1 : 0,
                            transition: 'opacity 0.15s, color 0.15s',
                            marginTop: multiline ? 2 : 0,
                        }}
                        onMouseEnter={e => {e.currentTarget.style.color = 'var(--ion-color-danger)';}}
                        onMouseLeave={e => {e.currentTarget.style.color = 'var(--ion-color-medium)';}}
                    >
                        <IonIcon icon={closeCircleOutline} style={{fontSize: sizes.iconSize}} />
                    </button>
                )}
            </div>

            {/* Bottom Row: Error/Helper + Character Count */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
                marginTop: 6,
            }}>
                {/* Error Message */}
                {error && errorMessage ? (
                    <div
                        id={`${id}-error`}
                        role="alert"
                        style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 12,
                            color: 'var(--ion-color-danger)',
                            flex: 1,
                        }}
                    >
                        {errorMessage}
                    </div>
                ) : helperText ? (
                    <div
                        style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 12,
                            color: 'var(--ion-color-medium)',
                            flex: 1,
                        }}
                    >
                        {helperText}
                    </div>
                ) : (
                    <div style={{flex: 1}} />
                )}

                {/* Character Count */}
                {showCounter && (
                    <div
                        style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 11,
                            color: maxLength && characterCount >= maxLength
                                ? 'var(--ion-color-danger)'
                                : 'var(--ion-color-medium)',
                            flexShrink: 0,
                        }}
                    >
                        {maxLength ? `${characterCount}/${maxLength}` : characterCount}
                    </div>
                )}
            </div>
        </div>
    );
};

TextInput.displayName = 'TextInput';

export default TextInput;
